import { useEffect, useRef } from 'react'
import type { ChartAppDependencies } from '@dx-private/dxchart5-react/dist/chart/chart-react-app'
import { loadLicensedWidgetRuntime, LicensedWidgetStartupError } from '../lib/chart/licensed-widget-loader'
import { licensedWidgetLog } from '../lib/debug/browser-logging'

interface LicensedChartWidgetHostProps {
  readonly dependencies: ChartAppDependencies
  readonly onApiCreated?: (api: any) => void
  readonly onApiDestroyed?: () => void
  readonly uiOverrides?: any
  readonly onInitializationError?: (error: Error) => void
  readonly className?: string
  readonly remountSignal?: number
}

const HiddenLicensedWidgetLogo = () => null

export function LicensedChartWidgetHost({
  dependencies,
  onApiCreated,
  onApiDestroyed,
  uiOverrides,
  onInitializationError,
  className,
  remountSignal,
}: LicensedChartWidgetHostProps) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const onApiCreatedRef = useRef(onApiCreated)
  const onApiDestroyedRef = useRef(onApiDestroyed)
  const onInitializationErrorRef = useRef(onInitializationError)

  onApiCreatedRef.current = onApiCreated
  onApiDestroyedRef.current = onApiDestroyed
  onInitializationErrorRef.current = onInitializationError

  useEffect(() => {
    let cancelled = false
    let destroyWidget: (() => void) | null = null
    let publishedApi: any = null
    const container = containerRef.current

    if (!container) {
      return
    }

    const initialize = async () => {
      try {
        const runtime = await loadLicensedWidgetRuntime()
        const effectiveDependencies: ChartAppDependencies = {
          ...dependencies,
          initialChartConfig: {
            ...(dependencies.initialChartConfig ?? {}),
            components: {
              ...(dependencies.initialChartConfig?.components ?? {}),
              waterMark: {
                ...(dependencies.initialChartConfig?.components?.waterMark ?? {}),
                visible: false,
              },
            },
          },
        }
        const effectiveUiOverrides = {
          ...(uiOverrides ?? {}),
          Logo: HiddenLicensedWidgetLogo,
        }
        let createdApi: any = null
        const widgetProps = {
          dependencies: effectiveDependencies,
          xAxisEnabled: true,
          yAxisEnabled: true,
          onAPICreated: (api: any) => {
            createdApi = api
          },
          uiOverrides: effectiveUiOverrides,
        } as unknown as import('@dx-private/dxchart5-react-widget/dist/widget.config').ChartReactWidgetProps
        const widgetInstance = await runtime.createWidget!(container, widgetProps)

        if (cancelled) {
          // StrictMode or rapid dependency churn can resolve createWidget after this effect was cleaned up.
          // If the original container is gone, skip destroy to avoid vendor DOM teardown races.
          if (!container.isConnected) {
            return
          }

          try {
            widgetInstance.destroy()
          } catch {
            // ignore teardown races while the effect is being replaced
          }
          return
        }

        if (createdApi) {
          publishedApi = createdApi
          onApiCreatedRef.current?.(createdApi)
        }

        destroyWidget = () => {
          try {
            widgetInstance.destroy()
          } catch {
            // ignore teardown races caused by DOM churn in strict-mode remount flows
          }
        }
      } catch (error) {
        if (cancelled) {
          return
        }

        const normalizedError =
          error instanceof LicensedWidgetStartupError
            ? error
            : new LicensedWidgetStartupError(
                error instanceof Error
                  ? error.message
                  : 'Licensed widget initialization failed.',
                [],
              )
        licensedWidgetLog('startup failed', {
          message: normalizedError.message,
          diagnostics: normalizedError.diagnostics,
        })
        onInitializationErrorRef.current?.(normalizedError)
      }
    }

    void initialize()

    return () => {
      cancelled = true
      if (publishedApi) {
        onApiDestroyedRef.current?.()
        publishedApi = null
      }
      destroyWidget?.()
      destroyWidget = null
    }
  }, [dependencies, uiOverrides, remountSignal])

  return (
    <div
      ref={containerRef}
      className={className}
      data-chart-host="licensed"
      style={{ width: '100%', height: '100%' }}
    />
  )
}
